import type { DesignFamilyId } from "../../design-rules/select-design-family.js";
import type { SiteBlueprint } from "../../types.js";

import baseCss from "./base.css?raw";

import { renderModernBusinessHome } from "./home.js";
import { MODERN_BUSINESS_THEMES, themeCss } from "./themes.js";

export type ModernBusinessRenderResult = {
  templateId: DesignFamilyId;
  designFamilyId: DesignFamilyId;
  designFamilyLabel: string;
  html: string;
  css: string;
};

const STOCK_IMAGES: Record<DesignFamilyId, string> = {
  "modern-blue-v1": "/studio/assets/stock/modern-business/blue-hero.webp",

  "modern-sage-v1": "/studio/assets/stock/modern-business/sage-hero.webp",

  "modern-soft-pink-v1":
    "/studio/assets/stock/modern-business/soft-pink-hero.webp",

  "modern-navy-v1": "/studio/assets/stock/modern-business/navy-hero.webp",

  "modern-charcoal-v1":
    "/studio/assets/stock/modern-business/charcoal-hero.webp",
};

function escapeAttribute(value: string): string {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll('"', "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function pageTitle(blueprint: SiteBlueprint): string {
  const name = String(blueprint.businessName || "").trim() || "Your Business";

  const explicitTitle = String(blueprint.title || "").trim();

  return explicitTitle || name;
}

function documentDescription(blueprint: SiteBlueprint): string {
  const description = String(blueprint.description || "").trim();

  if (description) return description;

  return "Professional service, thoughtful solutions, and a straightforward experience from start to finish.";
}

function navigationScript(): string {
  return `
    <script>
      (() => {
        const button =
          document.querySelector(".menu-button");

        const navigation =
          document.querySelector(".nav");

        if (!button || !navigation) return;

        button.addEventListener("click", () => {
          const isOpen =
            navigation.classList.toggle("open");

          button.setAttribute(
            "aria-expanded",
            String(isOpen),
          );
        });

        navigation
          .querySelectorAll("a")
          .forEach((link) => {
            link.addEventListener("click", () => {
              navigation.classList.remove("open");

              button.setAttribute(
                "aria-expanded",
                "false",
              );
            });
          });
      })();
    </script>
  `;
}

export function renderModernBusiness(
  blueprint: SiteBlueprint,
  familyId: DesignFamilyId = "modern-blue-v1",
): ModernBusinessRenderResult {
  const theme =
    MODERN_BUSINESS_THEMES[familyId] ??
    MODERN_BUSINESS_THEMES["modern-blue-v1"];

  const stockImageUrl =
    STOCK_IMAGES[familyId] ?? STOCK_IMAGES["modern-blue-v1"];

  const body = renderModernBusinessHome({
    blueprint,
    stockImageUrl,
  });

  /*
   * base.css is the exact shared stylesheet from
   * the PeachSites source design.
   *
   * themeCss is deliberately placed AFTER baseCss
   * so Blue / Sage / Soft Pink / Navy / Charcoal
   * override only the original color variables.
   */
  const css = `
    ${baseCss}

    ${themeCss(familyId)}
  `;

  const html = `
    <!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8">

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        >

        <title>
          ${escapeAttribute(pageTitle(blueprint))}
        </title>

        <meta
          name="description"
          content="${escapeAttribute(documentDescription(blueprint))}"
        >

        <style>
          ${css}
        </style>
      </head>

      <body
        data-design-family="${familyId}"
        data-design-label="${escapeAttribute(theme.label)}"
        data-panderr-template="modern-business"
      >
        ${body}

        ${navigationScript()}
      </body>
    </html>
  `;

  return {
    templateId: familyId,
    designFamilyId: familyId,
    designFamilyLabel: theme.label,
    html,
    css,
  };
}
